import { apiGet, apiPost, apiPatch, apiDelete, apiClient } from './client';

// 권위: docs/ARCH_DASHBOARD.md §4.9 / §6.2 — 알림 설정 (화면 9)
// 엔드포인트 계약 (Spring Boot):
//   GET    /api/v1/alert-config                        → AlertConfigResponse
//   PUT    /api/v1/alert-config                        전체 덮어쓰기
//   PATCH  /api/v1/alert-config/matrix                 Severity×Channel 매트릭스만
//   PATCH  /api/v1/alert-config/dnd                    DND 블록만
//   PATCH  /api/v1/alert-config/morning-summary        아침 요약만
//   PATCH  /api/v1/alert-config/warn-thresholds        빈발 WARN 에스컬레이션만
//   POST   /api/v1/alert-config/recipients             recipient 1건 생성
//   PATCH  /api/v1/alert-config/recipients/{id}        recipient 부분 수정
//   DELETE /api/v1/alert-config/recipients/{id}        실제로는 enabled=false soft-delete
//   GET    /api/v1/alert-config/history?page=&size=    alert_config_history 페이지네이션
//
// 서버 ↔ 클라이언트는 모두 camelCase(JSON). envelope 언랩은 apiClient 가 처리.

// ── 도메인 타입 ─────────────────────────────────────────

export type Severity = 'FATAL' | 'ERROR' | 'WARN' | 'INFO';
export type Channel = 'teams' | 'email' | 'webpush' | 'inapp';
export type NotifyChannel = 'email' | 'teams' | 'webpush';

export interface ChannelFlags {
  teams: boolean;
  email: boolean;
  webpush: boolean;
  inapp: boolean;
}

export type MatrixBySeverity = Record<Severity, ChannelFlags>;

export interface DndConfig {
  enabled: boolean;
  startHhmm: string; // "HH:mm"
  endHhmm: string;
  appliesTo: Array<'INFO' | 'WARN'>;
}

export interface MorningSummaryConfig {
  enabled: boolean;
  sendHhmm: string;
  includeSeverities: Severity[];
  channels: NotifyChannel[];
}

export interface WarnThresholdsConfig {
  enabled: boolean;
  windowMinutes: number;
  frequencyThreshold: number;
  escalateTo: 'ERROR' | 'FATAL';
}

export interface RecipientItem {
  recipientId: number;
  channel: NotifyChannel;
  address: string;
  displayName: string | null;
  severities: Severity[];
  enabled: boolean;
  createdAt: string;
  createdBy: string | null;
}

export interface AlertConfigResponse {
  matrix: MatrixBySeverity;
  dnd: DndConfig;
  morningSummary: MorningSummaryConfig;
  warnThresholds: WarnThresholdsConfig;
  recipients: RecipientItem[];
  updatedAt: string;
  updatedBy: string | null;
}

// ── 요청 바디 타입 ──────────────────────────────────────

export type MatrixPatchBody = { matrix: MatrixBySeverity };
export type DndPatchBody = { dnd: DndConfig };
export type MorningSummaryPatchBody = { morningSummary: MorningSummaryConfig };
export type WarnThresholdsPatchBody = { warnThresholds: WarnThresholdsConfig };

export interface RecipientCreateBody {
  channel: NotifyChannel;
  address: string;
  displayName?: string | null;
  severities: Severity[];
  enabled?: boolean;
}

export type RecipientPatchBody = Partial<RecipientCreateBody>;

// ── 이력 ─────────────────────────────────────────────────

export type HistoryScope =
  | 'matrix'
  | 'dnd'
  | 'morning_summary'
  | 'warn_thresholds'
  | 'recipient';

export interface AlertConfigHistoryItem {
  // 권위: dashboard-api HistoryItem.changeId (DB 컬럼 change_id).
  changeId: number;
  changedAt: string;
  changedBy: string | null;
  scope: HistoryScope;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  note: string | null;
}

export interface AlertConfigHistoryResponse {
  items: AlertConfigHistoryItem[];
  total: number;
  page: number;
  size: number;
}

// ── 에러 핵심 코드 ──────────────────────────────────────
// 서버가 422 로 회신하며 envelope.error.code 에 담아 준다.
// FATAL 행에서 teams / email / webpush 가 모두 off 인 경우 서버가 이 코드로 거절.
export const ERR_FATAL_CHANNELS_EMPTY = 'fatal_channels_empty' as const;
export type AlertConfigErrorCode = typeof ERR_FATAL_CHANNELS_EMPTY;

// ── 엔드포인트 함수 (10 개) ─────────────────────────────

const BASE = '/api/v1/alert-config';

export function getAlertConfig(): Promise<AlertConfigResponse> {
  return apiGet<AlertConfigResponse>(BASE);
}

export async function putAlertConfig(
  body: AlertConfigResponse
): Promise<AlertConfigResponse> {
  // apiClient 헬퍼에 put 래퍼가 없어 직접 호출.
  const resp = await apiClient.put<AlertConfigResponse>(BASE, body);
  return resp.data;
}

export function patchMatrix(
  body: MatrixPatchBody
): Promise<AlertConfigResponse> {
  return apiPatch<AlertConfigResponse>(`${BASE}/matrix`, body);
}

export function patchDnd(body: DndPatchBody): Promise<AlertConfigResponse> {
  return apiPatch<AlertConfigResponse>(`${BASE}/dnd`, body);
}

export function patchMorningSummary(
  body: MorningSummaryPatchBody
): Promise<AlertConfigResponse> {
  return apiPatch<AlertConfigResponse>(`${BASE}/morning-summary`, body);
}

export function patchWarnThresholds(
  body: WarnThresholdsPatchBody
): Promise<AlertConfigResponse> {
  return apiPatch<AlertConfigResponse>(`${BASE}/warn-thresholds`, body);
}

export function createRecipient(
  body: RecipientCreateBody
): Promise<RecipientItem> {
  return apiPost<RecipientItem>(`${BASE}/recipients`, body);
}

export function updateRecipient(
  recipientId: number,
  body: RecipientPatchBody
): Promise<RecipientItem> {
  return apiPatch<RecipientItem>(`${BASE}/recipients/${recipientId}`, body);
}

export function deleteRecipient(recipientId: number): Promise<void> {
  return apiDelete<void>(`${BASE}/recipients/${recipientId}`);
}

export function getAlertConfigHistory(
  page = 0,
  size = 20
): Promise<AlertConfigHistoryResponse> {
  const params = new URLSearchParams({
    page: String(Math.max(0, page)),
    size: String(Math.max(1, Math.min(200, size))),
  });
  return apiGet<AlertConfigHistoryResponse>(
    `${BASE}/history?${params.toString()}`
  );
}
